/**
 * Country object returned on each user row in GET /api/admin/users index `data[]`.
 * @typedef {{ code: string, name: string, currency: string }} AdminUserCountryObject
 */

/**
 * @typedef {AdminUserCountryObject | null} AdminUserCountry
 */

/**
 * @typedef {object} AdminUsersIndexResponse
 * @property {unknown[]} data
 * @property {number} [totalPages]
 * @property {number} [totalRecords]
 */

const PER_PAGE_OPTIONS = [10, 20, 50];

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeCountryQueryParam(value) {
  if (value === null || value === undefined) return '';
  const s = String(value).trim().toUpperCase();
  if (s.length !== 2 || !/^[A-Z]{2}$/.test(s)) return '';
  return s;
}

/**
 * @param {AdminUserCountry} country
 * @returns {string}
 */
export function formatAdminUserCountryCell(country) {
  if (typeof country === 'string') {
    const trimmed = country.trim();
    if (!trimmed) return '-';
    if (trimmed.startsWith('{')) {
      try {
        country = JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }
  if (!country || typeof country !== 'object') return '-';
  const name = country.name != null ? String(country.name).trim() : '';
  return name || '-';
}

/**
 * @param {Record<string, string | string[] | null | undefined>} query
 * @returns {{
 *   keyword: string,
 *   currentPage: number,
 *   perPage: number,
 *   registration_date_from: string,
 *   registration_date_to: string,
 *   is_news_letter_subscribed: boolean | null,
 *   countryFilter: string
 * }}
 */
export function parseAdminUsersRouteQuery(query) {
  const q = query || {};

  const pick = (key) => {
    const raw = q[key];
    if (Array.isArray(raw)) return raw[0] != null ? String(raw[0]) : '';
    return raw != null ? String(raw) : '';
  };

  let currentPage = parseInt(pick('page'), 10);
  if (!Number.isFinite(currentPage) || currentPage < 1) currentPage = 1;

  let perPage = parseInt(pick('per_page'), 10);
  if (!PER_PAGE_OPTIONS.includes(perPage)) perPage = 20;

  const nl = pick('is_news_letter_subscribed');
  let is_news_letter_subscribed = null;
  if (nl === 'true') is_news_letter_subscribed = true;
  else if (nl === 'false') is_news_letter_subscribed = false;

  const countryRaw = pick('country');
  const countryFilter = normalizeCountryQueryParam(countryRaw);

  return {
    keyword: pick('keyword'),
    currentPage,
    perPage,
    registration_date_from: pick('registration_date_from'),
    registration_date_to: pick('registration_date_to'),
    is_news_letter_subscribed,
    countryFilter,
  };
}

/**
 * @param {{
 *   keyword: string,
 *   currentPage: number,
 *   perPage: number,
 *   registration_date_from: string,
 *   registration_date_to: string,
 *   is_news_letter_subscribed: boolean | null,
 *   countryFilter: string
 * }} state
 * @returns {Record<string, string>}
 */
export function serializeAdminUsersRouteQuery(state) {
  /** @type {Record<string, string>} */
  const q = {};

  if (state.keyword) q.keyword = state.keyword;
  if (state.currentPage > 1) q.page = String(state.currentPage);
  if (state.perPage !== 20) q.per_page = String(state.perPage);
  if (state.registration_date_from) q.registration_date_from = state.registration_date_from;
  if (state.registration_date_to) q.registration_date_to = state.registration_date_to;
  if (state.is_news_letter_subscribed === true) q.is_news_letter_subscribed = 'true';
  if (state.is_news_letter_subscribed === false) q.is_news_letter_subscribed = 'false';
  if (state.countryFilter) q.country = state.countryFilter;

  return q;
}

/**
 * @param {Record<string, string | string[] | null | undefined>} a
 * @param {Record<string, string | string[] | null | undefined>} b
 * @returns {boolean}
 */
export function adminUsersRouteQueriesEqual(a, b) {
  const norm = (x) => {
    const o = /** @type {Record<string, string>} */ ({});
    Object.keys(x || {}).sort().forEach((k) => {
      const v = x[k];
      const s = Array.isArray(v) ? (v[0] != null ? String(v[0]) : '') : v != null ? String(v) : '';
      if (s !== '') o[k] = s;
    });
    return o;
  };
  const A = norm(a);
  const B = norm(b);
  const keys = new Set([...Object.keys(A), ...Object.keys(B)]);
  for (const k of keys) {
    if ((A[k] || '') !== (B[k] || '')) return false;
  }
  return true;
}

/**
 * @param {{
 *   page: number,
 *   perPage: number,
 *   keyword: string,
 *   registration_date_from: string,
 *   registration_date_to: string,
 *   is_news_letter_subscribed: boolean | null,
 *   countryFilter: string
 * }} filters
 * @returns {Record<string, string | number | boolean>}
 */
export function buildAdminUsersListParams(filters) {
  const params = {
    page: filters.page,
    per_page: filters.perPage,
    keyword: filters.keyword,
    registration_date_from: filters.registration_date_from,
    registration_date_to: filters.registration_date_to,
    is_news_letter_subscribed: filters.is_news_letter_subscribed,
    country: filters.countryFilter,
  };

  Object.keys(params).forEach((key) => {
    const v = params[key];
    if (v === null || v === undefined || v === '') {
      delete params[key];
    }
  });

  return params;
}

/**
 * Same filter shape as the list endpoint for GET /admin/export/users.
 * @param {{
 *   keyword: string,
 *   registration_date_from: string,
 *   registration_date_to: string,
 *   is_news_letter_subscribed: boolean | null,
 *   countryFilter: string
 * }} filters
 * @returns {Record<string, string | boolean>}
 */
export function buildAdminUsersExportParams(filters) {
  const params = {
    keyword: filters.keyword,
    registration_date_from: filters.registration_date_from,
    registration_date_to: filters.registration_date_to,
    is_news_letter_subscribed: filters.is_news_letter_subscribed,
    country: filters.countryFilter,
  };

  Object.keys(params).forEach((key) => {
    const v = params[key];
    if (v === null || v === undefined || v === '') {
      delete params[key];
    }
  });

  return params;
}
